import { observer } from 'mobx-react-lite';
import { useIntl } from 'react-intl';
import { useMessagingStore } from '../hooks/useMessagingStore.tsx';
import { SurfaceRenderer } from '../a2ui/SurfaceRenderer.tsx';
import { SurfaceHeader } from '../a2ui/blocks/SurfaceHeader.tsx';
import { messages } from '../localization/messages.ts';
import { Button } from '../shadcdn/button.tsx';
import { TextPage } from './TextPage.tsx';
import { processPageFor } from './process/index.ts';
import type { LoadingLine } from './loading-line.ts';
import type { StageView } from './stage-view.ts';
import type { buildTurnIndex } from './turn-index.ts';

/** Blocking arrival failures stay visible without opening activity details. */
function ArrivalNotice({ line, onRetry }: { line: LoadingLine | null; onRetry: () => void }) {
  const intl = useIntl();
  if (!line || line.kind === 'connecting' || line.kind === 'preparing' || line.kind === 'working') {
    return null;
  }
  return (
    <div className="flex min-h-[40vh] min-w-0 flex-col items-center justify-center gap-2 px-4 text-center">
      <p aria-live="polite" className="w-full max-w-md wrap-anywhere text-sm text-muted-foreground">
        <span
          key={line.text}
          className="animate-in fade-in duration-500 motion-reduce:animate-none"
        >
          {line.text}
        </span>
      </p>
      {'retry' in line && line.retry ? (
        <Button type="button" variant="link" size="surface" onClick={onRetry} className="underline">
          {intl.formatMessage(messages.retry)}
        </Button>
      ) : null}
    </div>
  );
}

interface StagePageProps {
  view: StageView;
  turns: ReturnType<typeof buildTurnIndex>;
  loadingLine: LoadingLine | null;
  onSubmit: () => void;
}

/** Maps the shared stage resolver's selected content to the agent's real renderer. */
export const StagePage = observer(function StagePage({
  view,
  turns,
  loadingLine,
  onSubmit,
}: StagePageProps) {
  const store = useMessagingStore();
  const intl = useIntl();
  const retry = () => window.location.reload();
  switch (view.kind) {
    case 'surface':
      return (
        <SurfaceRenderer
          surfaceId={view.surfaceId}
          snapshot={view.snapshot}
          contentId={view.contentId}
          onSubmit={onSubmit}
        />
      );
    case 'process': {
      const part = store.activeProcessPart;
      const ProcessPage = part ? processPageFor(part.componentName) : undefined;
      const request = turns[view.turnIndex]?.request;
      return (
        <>
          {request ? <SurfaceHeader title={request} /> : null}
          {ProcessPage && part ? <ProcessPage part={part} /> : null}
        </>
      );
    }
    case 'text': {
      const turn = turns[view.turnIndex];
      const error = store.lastRunError;
      if (error && turn?.responseId === error.responseId) {
        return null;
      }
      return <TextPage turn={turn} />;
    }
    case 'loading':
      return <ArrivalNotice line={loadingLine} onRetry={retry} />;
    case 'unavailable':
      return <p role="status">{intl.formatMessage(messages.viewCouldNotDisplay)}</p>;
  }
});
